import type { AcquisitionsOutcome, BootstrapPayload, ConsentBasis, GlobalSettings, LeadDisposition, QualificationKey, RunDetail } from "../shared/types";

export interface ImportLeadInput {
  phone: string;
  address: string;
  sellerName?: string;
}

export interface ImportRunsResult {
  created: Array<{ id: string; sellerName: string | null; phone: string; address: string }>;
  duplicates: Array<{ row: number; existingRunId: string }>;
  rejected: Array<{ row: number; reason: string }>;
  counts: { created: number; duplicates: number; rejected: number };
}

export class ApiError extends Error {
  status: number;
  reasons: string[];

  constructor(message: string, status: number, reasons: string[] = []) {
    super(message);
    this.status = status;
    this.reasons = reasons;
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(path, {
    credentials: "same-origin",
    ...init,
    headers: { "content-type": "application/json", ...(init?.headers ?? {}) }
  });
  const payload = response.status === 204 ? null : await response.json().catch(() => null);
  if (!response.ok) throw new ApiError(payload?.error ?? `Request failed with HTTP ${response.status}`, response.status, payload?.reasons ?? []);
  return payload as T;
}

export const api = {
  bootstrap: (runId?: string) => request<BootstrapPayload>(`/api/bootstrap${runId ? `?runId=${encodeURIComponent(runId)}` : ""}`),
  login: (accessKey: string) => request<{ ok: true }>("/api/auth/login", { method: "POST", body: JSON.stringify({ accessKey }) }),
  logout: () => request<{ ok: true }>("/api/auth/logout", { method: "POST", body: "{}" }),
  createRun: (input: { phone: string; address: string; sellerName?: string; consentBasis: ConsentBasis }) => request<RunDetail>("/api/runs", { method: "POST", body: JSON.stringify(input) }),
  importRuns: (leads: ImportLeadInput[]) => request<ImportRunsResult>("/api/runs/import", { method: "POST", body: JSON.stringify({ leads }) }),
  reply: (id: string, body: string) => request<RunDetail>(`/api/runs/${id}/reply`, { method: "POST", body: JSON.stringify({ body }) }),
  call: (id: string) => request<RunDetail>(`/api/runs/${id}/call`, { method: "POST", body: "{}" }),
  consent: (id: string, consentBasis: Exclude<ConsentBasis, "review_required">) => request<RunDetail>(`/api/runs/${id}/consent`, { method: "POST", body: JSON.stringify({ consentBasis }) }),
  qualification: (id: string, key: QualificationKey, value: string) => request<RunDetail>(`/api/runs/${id}/qualification`, { method: "PATCH", body: JSON.stringify({ key, value }) }),
  pause: (id: string, paused: boolean, note = "") => request<RunDetail>(`/api/runs/${id}/pause`, { method: "POST", body: JSON.stringify({ paused, note }) }),
  nurture: (id: string, input: { reason: string; resumeAt: string; expectedUpdatedAt: string }) => request<RunDetail>(`/api/runs/${id}/nurture`, { method: "POST", body: JSON.stringify(input) }),
  disposition: (id: string, input: { action: "close" | "reopen"; disposition?: LeadDisposition; note?: string; expectedUpdatedAt: string }) => request<RunDetail>(`/api/runs/${id}/disposition`, { method: "POST", body: JSON.stringify(input) }),
  dnc: (id: string, confirmation: string) => request<RunDetail>(`/api/runs/${id}/dnc`, { method: "POST", body: JSON.stringify({ confirmation }) }),
  appointment: (id: string, appointmentAt: string, expectedUpdatedAt: string) => request<RunDetail>(`/api/runs/${id}/appointment`, { method: "POST", body: JSON.stringify({ appointmentAt, expectedUpdatedAt }) }),
  appointmentReminder: (id: string, action: "schedule" | "cancel", expectedUpdatedAt: string) => request<RunDetail>(`/api/runs/${id}/appointment-reminder`, { method: "POST", body: JSON.stringify({ action, expectedUpdatedAt }) }),
  handoff: (id: string) => request<RunDetail>(`/api/runs/${id}/handoff`, { method: "POST", body: "{}" }),
  acquisitionsOutcome: (id: string, input: { outcome: AcquisitionsOutcome; note: string; nextAction: string; nextActionAt: string }) => request<RunDetail>(`/api/runs/${id}/acquisitions-outcome`, { method: "POST", body: JSON.stringify(input) }),
  acquisitionsTask: (id: string, input: { action: "reschedule" | "complete"; expectedNextAction: string; expectedNextActionAt: string; note: string; nextAction: string; nextActionAt: string }) => request<RunDetail>(`/api/runs/${id}/acquisitions-task`, { method: "POST", body: JSON.stringify(input) }),
  masterSend: (enabled: boolean, confirmation = "") => request<GlobalSettings>("/api/settings/master-send", { method: "POST", body: JSON.stringify({ enabled, confirmation }) })
};
